import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/i18n";
import AppShell from "@/components/AppShell";
import TemplatesBar from "@/components/TemplatesBar";
import { useCanvasScale, eventToPreviewCoords } from "@/lib/canvasScale";
import { supportsFolderWrite, writeZipToFolder, downloadZip } from "@/lib/folderWriter";
import FolderWriteErrorDialog from "@/components/FolderWriteErrorDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Upload, FileText, Trash2, MousePointerClick, Download, FolderOpen,
  ChevronLeft, ChevronRight, Loader2, PlusCircle,
} from "lucide-react";

export default function EditorPage() {
  const t = useT();
  // Template PDF preview
  const [previewPages, setPreviewPages] = useState([]); // [{page,image,pdf_width,pdf_height,preview_width,preview_height}]
  const [scale, setScale] = useState(1); // preview_dpi/72
  const [templateName, setTemplateName] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Fields placed on the template (in preview px)
  const [fields, setFields] = useState([]); // {id,page,x,y,text,font_size}
  const [selectedId, setSelectedId] = useState(null);

  // Batch files
  const [batchFiles, setBatchFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [folderError, setFolderError] = useState(null);
  const lastBlobRef = useRef({ blob: null, filename: "" });

  const canvasRef = useRef(null);
  const dragRef = useRef(null); // {id, offsetX, offsetY}
  const textInputRef = useRef(null);

  const page = previewPages[currentPage];
  const { outerRef, displayScale } = useCanvasScale(page?.preview_width);

  const handleTemplateUpload = async (file) => {
    if (!file) return;
    setLoadingPreview(true);
    setPreviewPages([]);
    setFields([]);
    setSelectedId(null);
    setCurrentPage(0);
    setTemplateName(file.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pdf/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreviewPages(data.pages);
      setScale(data.scale);
      toast.success(t("editor.toasts.templateLoaded", { n: data.pages.length }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const onCanvasClick = (e) => {
    if (!page) return;
    // Ignore clicks on existing markers
    if (e.target.closest("[data-field-marker]")) return;
    const { x, y } = eventToPreviewCoords(e, canvasRef.current);
    const id = crypto.randomUUID();
    const newField = {
      id,
      page: currentPage,
      x,
      y,
      text: t("editor.defaultText"),
      font_size: 24, // preview px (150 dpi) -> ~11.5pt PDF
    };
    setFields((prev) => [...prev, newField]);
    setSelectedId(id);
  };

  const updateField = (id, patch) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const deleteField = (id) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const onMarkerMouseDown = (e, f) => {
    e.stopPropagation();
    setSelectedId(f.id);
    const { x, y } = eventToPreviewCoords(e, canvasRef.current);
    dragRef.current = {
      id: f.id,
      offsetX: x - f.x,
      offsetY: y - f.y,
    };
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
  };

  const onDragMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag || !canvasRef.current) return;
    const intrinsicW = canvasRef.current.offsetWidth || 1;
    const intrinsicH = canvasRef.current.offsetHeight || 1;
    const { x: mx, y: my } = eventToPreviewCoords(e, canvasRef.current);
    const x = Math.max(0, Math.min(intrinsicW, mx - drag.offsetX));
    const y = Math.max(0, Math.min(intrinsicH, my - drag.offsetY));
    setFields((prev) => prev.map((f) => (f.id === drag.id ? { ...f, x, y } : f)));
  }, []);

  const onDragEnd = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
  }, [onDragMove]);

  const currentPageFields = useMemo(
    () => fields.filter((f) => f.page === currentPage),
    [fields, currentPage]
  );

  // Focus the text input when a new field is selected, WITHOUT triggering the
  // browser's auto-scroll-into-view (which used to yank the PDF out of the
  // viewport and make the canvas appear blank).
  useEffect(() => {
    if (selectedId && textInputRef.current) {
      try {
        textInputRef.current.focus({ preventScroll: true });
        textInputRef.current.select?.();
      } catch (_) {}
    }
  }, [selectedId]);

  // Batch handling
  const addBatchFiles = (fileList) => {
    const arr = Array.from(fileList).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    setBatchFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...arr.filter((f) => !existing.has(f.name + f.size))];
    });
  };
  const removeBatchFile = (idx) => setBatchFiles((prev) => prev.filter((_, i) => i !== idx));

  const onDropBatch = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) addBatchFiles(e.dataTransfer.files);
  };

  const runBatch = async () => {
    if (!batchFiles.length) {
      toast.error(t("editor.toasts.needPdfs"));
      return;
    }
    if (!fields.length) {
      toast.error(t("editor.toasts.needFields"));
      return;
    }
    setProcessing(true);
    try {
      const fd = new FormData();
      batchFiles.forEach((f) => fd.append("files", f));
      const cleanedFields = fields.map((f) => ({
        page: f.page, x: f.x, y: f.y, text: f.text, font_size: f.font_size,
      }));
      fd.append("fields", JSON.stringify(cleanedFields));
      fd.append("scale", String(scale));
      const res = await api.post("/pdf/process", fd, {
        responseType: "blob",
        headers: { "Content-Type": "multipart/form-data" },
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const baseName = templateName || "template";
      const zipName = `${baseName.replace(/\.[a-zA-Z0-9]+$/, "")}-output.zip`;
      lastBlobRef.current = { blob, filename: zipName };
      if (supportsFolderWrite()) {
        try {
          const result = await writeZipToFolder(blob, baseName);
          if (result.cancelled) {
            toast.info(t("common.saveCanceled"));
          } else {
            toast.success(t("editor.toasts.savedInFolder", { n: result.count, folder: result.folderName }));
          }
        } catch (folderErr) {
          // Detailed diagnostics for the user
          // eslint-disable-next-line no-console
          console.error("[folder-write]", folderErr, folderErr.diagnostics);
          setFolderError(folderErr);
        }
      } else {
        downloadZip(blob, zipName);
        toast.success(t("editor.toasts.archiveDownloaded", { n: batchFiles.length }));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setProcessing(false);
    }
  };

  const selectedField = fields.find((f) => f.id === selectedId);
  const outputBaseName = (templateName || "template").replace(/\.[a-zA-Z0-9]+$/, "");

  return (
    <AppShell subtitle={templateName || t("editor.subtitleEmpty")}>
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr_340px]" data-testid="editor-page">
        {/* Left sidebar: Steps + template upload */}
        <aside className="border-r border-white/[0.08] p-6 space-y-8 overflow-y-auto">
          <div>
            <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">
              {t("editor.step1")}
            </div>
            <h3 className="font-geist font-semibold text-2xl mt-2 mb-4">{t("editor.step1Title")}</h3>
            <label
              data-testid="template-dropzone"
              className="block border border-dashed border-white/15 hover:border-[#4D8AFF] hover:bg-[#4D8AFF]/[0.06] transition-colors p-6 cursor-pointer rounded-sm text-center"
            >
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                data-testid="template-file-input"
                onChange={(e) => handleTemplateUpload(e.target.files?.[0])}
              />
              {loadingPreview ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin text-[#4D8AFF]" />
                  <span className="font-geist-mono text-xs text-white/60">{t("editor.processing")}</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-6 w-6 text-[#4D8AFF]" />
                  <div className="font-geist-mono text-xs text-white">
                    {t("editor.dropHereCta")}
                  </div>
                  <div className="font-geist-mono text-[10px] text-white/40">
                    {t("editor.dropHereHint")}
                  </div>
                </div>
              )}
            </label>
          </div>

          <div>
            <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">
              {t("editor.step2")}
            </div>
            <h3 className="font-geist font-semibold text-2xl mt-2 mb-4">{t("editor.step2Title")}</h3>
            <label
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDropBatch}
              data-testid="batch-dropzone"
              className="block border border-dashed border-white/15 hover:border-[#4D8AFF] hover:bg-[#4D8AFF]/[0.06] transition-colors p-6 cursor-pointer rounded-sm text-center"
            >
              <input
                type="file"
                accept="application/pdf"
                multiple
                className="hidden"
                data-testid="batch-file-input"
                onChange={(e) => addBatchFiles(e.target.files)}
              />
              <FolderOpen className="h-6 w-6 text-[#4D8AFF] mx-auto mb-2" />
              <div className="font-geist-mono text-xs text-white">{t("editor.dropFolderCta")}</div>
              <div className="font-geist-mono text-[10px] text-white/40">{t("editor.dropFolderHint")}</div>
            </label>

            {batchFiles.length > 0 && (
              <div className="mt-4 border border-white/[0.08] rounded-sm divide-y divide-border max-h-56 overflow-y-auto" data-testid="batch-file-list">
                {batchFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-3.5 w-3.5 text-[#4D8AFF] flex-shrink-0" />
                      <span className="font-geist-mono truncate">{f.name}</span>
                    </div>
                    <button
                      data-testid={`remove-batch-file-${i}`}
                      onClick={() => removeBatchFile(i)}
                      className="text-[#E63946] hover:bg-[#E63946]/10 p-1 rounded-sm"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="font-geist-mono text-[10px] text-white/40 mt-2">
              {t("editor.filesSelected", { n: batchFiles.length })}
            </div>
          </div>

          <div>
            <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">
              {t("editor.step3")}
            </div>
            <h3 className="font-geist font-semibold text-2xl mt-2 mb-4">{t("editor.step3Title")}</h3>
            <Button
              data-testid="process-batch-btn"
              disabled={processing || !batchFiles.length || !fields.length || !previewPages.length}
              onClick={runBatch}
              className="w-full h-11 rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-medium"
            >
              {processing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("editor.processing")}</>
              ) : supportsFolderWrite() ? (
                <><Download className="h-4 w-4 mr-2" /> {t("common.saveFolder")}</>
              ) : (
                <><Download className="h-4 w-4 mr-2" /> {t("common.downloadZip")}</>
              )}
            </Button>
            <p className="font-geist-mono text-[10px] text-white/40 mt-2 leading-relaxed">
              {supportsFolderWrite()
                ? t("editor.outputFolderHint", { name: outputBaseName })
                : t("editor.outputFolderHintZip")}
            </p>
          </div>

          <TemplatesBar
            fields={fields}
            scale={scale}
            pageCount={previewPages.length || 1}
            canSave={fields.length > 0 && previewPages.length > 0}
            onLoad={(tpl) => {
              setFields(tpl.fields.map((f) => ({ ...f, id: crypto.randomUUID() })));
              setScale(tpl.scale);
            }}
          />
        </aside>

        {/* Center: PDF canvas */}
        <main ref={outerRef} className="p-8 dot-grid overflow-auto min-h-[600px]">
          {!page ? (
            <div className="h-full min-h-[500px] flex flex-col items-center justify-center text-center" data-testid="empty-canvas">
              <MousePointerClick className="h-12 w-12 text-[#4D8AFF] mb-6" strokeWidth={1.2} />
              <div className="font-geist font-semibold text-3xl text-white max-w-md">
                {t("editor.emptyCanvasTitle")}
              </div>
              <div className="font-geist-mono text-xs text-white/60 mt-4 max-w-md">
                {t("editor.emptyCanvasBody")}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              {previewPages.length > 1 && (
                <div className="flex items-center gap-4 mb-4 font-geist-mono text-xs">
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="prev-page-btn"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    className="rounded-sm hover:bg-white/[0.06]"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span>{t("editor.pageIndicator", { current: currentPage + 1, total: previewPages.length })}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="next-page-btn"
                    disabled={currentPage >= previewPages.length - 1}
                    onClick={() => setCurrentPage((p) => Math.min(previewPages.length - 1, p + 1))}
                    className="rounded-sm hover:bg-white/[0.06]"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}

              <div
                style={{
                  width: page.preview_width * displayScale,
                  height: page.preview_height * displayScale,
                  overflow: "hidden",
                }}
              >
                <div
                  ref={canvasRef}
                  data-testid="pdf-canvas"
                  onClick={onCanvasClick}
                  className="relative bg-white border border-white/25 shadow-[0_0_40px_-10px_rgba(77,138,255,0.35)] cursor-crosshair"
                  style={{
                    width: page.preview_width,
                    height: page.preview_height,
                    transform: `scale(${displayScale})`,
                    transformOrigin: "top left",
                  }}
                >
                  <img
                    key={page.image.slice(0, 64)}
                    src={page.image}
                    alt="template"
                    draggable={false}
                    className="absolute inset-0 w-full h-full pointer-events-none select-none"
                  />
                  {currentPageFields.map((f) => (
                    <div
                      key={f.id}
                      data-field-marker
                      data-testid={`field-marker-${f.id}`}
                      onMouseDown={(e) => onMarkerMouseDown(e, f)}
                      onClick={(e) => { e.stopPropagation(); setSelectedId(f.id); }}
                      className={`field-marker ${selectedId === f.id ? "selected" : ""}`}
                      style={{
                        left: f.x,
                        top: f.y,
                        fontSize: f.font_size,
                        lineHeight: 1.1,
                      }}
                    >
                      {f.text || "…"}
                    </div>
                  ))}
                </div>
              </div>

              <div className="font-geist-mono text-[10px] text-white/40 mt-3">
                {t("editor.canvasMeta", {
                  w: page.pdf_width.toFixed(0),
                  h: page.pdf_height.toFixed(0),
                  pw: page.preview_width,
                  ph: page.preview_height,
                })}
              </div>
            </div>
          )}
        </main>

        {/* Right sidebar: Field list */}
        <aside className="border-l border-white/[0.08] p-6 overflow-y-auto bg-[#050B1F]/40">
          <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">
            {t("editor.rightTitle")}
          </div>
          <h3 className="font-geist font-semibold text-2xl mt-2 mb-4">{t("editor.rightSub")}</h3>

          {fields.length === 0 ? (
            <div className="border border-dashed border-white/[0.08] rounded-sm p-6 text-center font-geist-mono text-xs text-white/40">
              <PlusCircle className="h-5 w-5 mx-auto mb-2 text-[#4D8AFF]" />
              {t("editor.noFieldsHint")}
            </div>
          ) : (
            <div className="space-y-2" data-testid="field-list">
              {fields.map((f, idx) => (
                <button
                  key={f.id}
                  data-testid={`field-list-item-${f.id}`}
                  onClick={() => { setCurrentPage(f.page); setSelectedId(f.id); }}
                  className={`w-full text-left px-3 py-2 border rounded-sm transition-colors ${
                    selectedId === f.id
                      ? "border-[#002FA7] bg-[#002FA7]/5"
                      : "border-white/[0.08] hover:border-[#111111]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-geist-mono text-[10px] text-white/40">
                      #{String(idx + 1).padStart(2, "0")} · pag {f.page + 1}
                    </span>
                    <span className="font-geist-mono text-[10px] text-white/60">
                      x{Math.round(f.x)} y{Math.round(f.y)}
                    </span>
                  </div>
                  <div className="text-sm truncate mt-1">{f.text || t("editor.emptyField")}</div>
                </button>
              ))}
            </div>
          )}

          {selectedField && (
            <div className="mt-6 pt-6 border-t border-white/[0.08] space-y-4" data-testid="field-editor">
              <div className="font-geist-mono text-[10px] tracking-widest uppercase text-white/40">
                {t("editor.editField")}
              </div>
              <div>
                <Label className="text-xs font-geist-mono uppercase tracking-wider text-white/60">{t("editor.textLabel")}</Label>
                <Input
                  data-testid="field-text-input"
                  ref={textInputRef}
                  value={selectedField.text}
                  onChange={(e) => updateField(selectedField.id, { text: e.target.value })}
                  className="mt-2 rounded-sm h-10"
                />
              </div>
              <div>
                <Label className="text-xs font-geist-mono uppercase tracking-wider text-white/60">
                  {t("editor.fontSizeLabel")}
                </Label>
                <Input
                  data-testid="field-fontsize-input"
                  type="number"
                  min={8}
                  max={200}
                  value={selectedField.font_size}
                  onChange={(e) => updateField(selectedField.id, { font_size: Number(e.target.value) || 12 })}
                  className="mt-2 rounded-sm h-10 font-geist-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-geist-mono uppercase tracking-wider text-white/60">X</Label>
                  <Input
                    data-testid="field-x-input"
                    type="number"
                    value={Math.round(selectedField.x)}
                    onChange={(e) => updateField(selectedField.id, { x: Number(e.target.value) || 0 })}
                    className="mt-2 rounded-sm h-10 font-geist-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs font-geist-mono uppercase tracking-wider text-white/60">Y</Label>
                  <Input
                    data-testid="field-y-input"
                    type="number"
                    value={Math.round(selectedField.y)}
                    onChange={(e) => updateField(selectedField.id, { y: Number(e.target.value) || 0 })}
                    className="mt-2 rounded-sm h-10 font-geist-mono"
                  />
                </div>
              </div>
              <Button
                variant="ghost"
                data-testid="delete-field-btn"
                onClick={() => deleteField(selectedField.id)}
                className="w-full rounded-sm text-[#E63946] hover:bg-[#E63946]/10 hover:text-[#E63946]"
              >
                <Trash2 className="h-4 w-4 mr-2" /> {t("editor.deleteField")}
              </Button>
            </div>
          )}
        </aside>
      </div>
      <FolderWriteErrorDialog
        error={folderError}
        onClose={() => setFolderError(null)}
        onDownloadZip={() => {
          if (lastBlobRef.current.blob) {
            downloadZip(lastBlobRef.current.blob, lastBlobRef.current.filename);
            toast.success(t("common.archiveDownloaded"));
          }
        }}
      />
    </AppShell>
  );
}
